import { useState, useEffect } from 'react';
import { Tabs, Form, Input, Button, message, Modal, QRCode } from 'antd';
import { authAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import { validateUsername, validateEmail, validatePassword } from '../utils/validator';
import { useSearchParams } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';

const Iden = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { login: authLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');

  // 微信扫码登录相关状态
  const [wechatModalVisible, setWechatModalVisible] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [wechatStatus, setWechatStatus] = useState('waiting'); // waiting, scanned, need_bind, need_register
  const [openid, setOpenid] = useState('');
  const [wechatForm] = Form.useForm();
  const [qrId, setQrId] = useState(''); // 存储qrId用于测试
  const [useWechat, setUseWechat] = useState(false); // 是否使用真实微信登录

  // 设置网页标题
  useDocumentTitle('登录/注册', {
    prefix: '',
    suffix: ' - rtycn'
  });

const onFinish = async (values) => {
    try {
      setLoading(true);
      const response = await authLogin(values);
      console.log('登录响应:', response); // 用于调试

      // 登录成功后，使用 window.location.href 强制跳转
      if (response && response.data) {
        const user = response.data.user;
        message.success('登录成功');
        setTimeout(() => {
          window.location.href = `/${user.username}/index/`;
        }, 100);
      }
    } catch (error) {
      // 根据axios拦截器的错误处理，显示详细的错误信息
      const errorMessage = error?.response?.data?.message || error?.message || '登录失败，请检查网络连接';
      console.error('登录错误:', error);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (values) => {
    try {
      // 前端验证
      const usernameResult = validateUsername(values.username);
      if (!usernameResult.valid) {
        message.error(usernameResult.message);
        return;
      }

      const emailResult = validateEmail(values.email);
      if (!emailResult.valid) {
        message.error(emailResult.message);
        return;
      }

      const passwordResult = validatePassword(values.password);
      if (!passwordResult.valid) {
        message.error(passwordResult.message);
        return;
      }

      setLoading(true);
      const response = await authAPI.register({
        username: values.username,
        name: values.username,
        password: values.password,
        email: values.email,
        nickname: values.username
      });

      console.log('注册响应:', response); // 用于调试

      // 检查后端返回的success字段，如果是false说明注册失败
      if (response && response.success === false) {
        const errorMsg = response.message || '注册失败';
        message.error(errorMsg);
        return;
      }

      message.success('注册成功，请登录');
      form.setFieldsValue({ username: values.username, password: '' });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || '注册失败，请检查网络连接';
      console.error('注册错误:', error);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onResetPassword = async (values) => {
    try {
      setLoading(true);
      await authAPI.resetPassword({ email: values.email });
      message.success('密码重置链接已发送到您的邮箱，请查收');
    } catch (error) {
      console.error('重置密码失败:', error);
      message.error(error.message || '重置密码失败，请检查邮箱地址后重试');
    } finally {
      setLoading(false);
    }
  };

  const onConfirmResetPassword = async (values) => {
    try {
      setLoading(true);
      await authAPI.confirmResetPassword({
        token: resetToken,
        newPassword: values.newPassword
      });
      message.success('密码重置成功，请使用新密码登录');
      form.resetFields();
    } catch (error) {
      console.error('重置密码失败:', error);
      message.error(error.message || '密码重置失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resetToken) {
      form.setFieldsValue({ activeTab: '4' });
    }
  }, [resetToken]);

  // 生成微信登录二维码
  const handleWechatLogin = async () => {
    try {
      const response = await authAPI.generateWechatQRCode();
      if (response.data?.qrcode) {
        setQrCode(response.data.qrcode);
        setQrId(response.data.qrId);
        setUseWechat(response.data.useWechat || false);
        setWechatModalVisible(true);
        // 开始轮询检查扫码状态
        startPolling(response.data.qrId);
      }
    } catch (error) {
      message.error('生成二维码失败');
    }
  };

  // 轮询检查扫码状态
  let pollTimer = null;
  const startPolling = (qrId) => {
    if (pollTimer) clearInterval(pollTimer);

    pollTimer = setInterval(async () => {
      try {
        const response = await authAPI.checkWechatLoginStatus(qrId);
        const status = response.data?.status;
        console.log('扫码状态:', response.data);

        if (status === 'scanned') {
          setWechatStatus('scanned');
          setOpenid(response.data?.openid || '');
        } else if (status === 'need_bind') {
          setWechatStatus('need_bind');
          setOpenid(response.data?.openid || '');
          clearInterval(pollTimer);
        } else if (status === 'need_register') {
          setWechatStatus('need_register');
          setOpenid(response.data?.openid || '');
          clearInterval(pollTimer);
        } else if (status === 'confirmed') {
          // 用户已存在，直接登录
          const user = response.data?.user;
          const token = response.data?.token;
          if (user && token) {
            localStorage.setItem('auth_token', token);
            localStorage.setItem('current_user', JSON.stringify(user));
            setWechatModalVisible(false);
            setTimeout(() => {
              window.location.href = `/${user.username}/index/`;
            }, 100);
          }
          clearInterval(pollTimer);
        } else if (status === 'expired') {
          message.error('二维码已过期，请重新生成');
          clearInterval(pollTimer);
        }
      } catch (error) {
        console.error('检查扫码状态失败:', error);
        message.error('检查扫码状态失败: ' + (error.message || '未知错误'));
      }
    }, 2000); // 每2秒检查一次
  };

  // 绑定已有账号
  const handleBindAccount = async (values) => {
    try {
      setLoading(true);
      const response = await authAPI.bindWechatAccount({
        username: values.username,
        password: values.password,
        openid: openid
      });

      if (response.data?.token) {
        message.success('绑定成功');
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('current_user', JSON.stringify(response.data.user));
        setWechatModalVisible(false);
        setTimeout(() => {
          window.location.href = `/${response.data.user.username}/index/`;
        }, 100);
      }
    } catch (error) {
      message.error(error.response?.data?.message || '绑定失败');
    } finally {
      setLoading(false);
    }
  };

  // 使用微信注册新账号
  const handleWechatRegister = async (values) => {
    try {
      // 前端验证
      const usernameResult = validateUsername(values.username);
      if (!usernameResult.valid) {
        message.error(usernameResult.message);
        return;
      }

      const emailResult = validateEmail(values.email);
      if (!emailResult.valid) {
        message.error(emailResult.message);
        return;
      }

      const passwordResult = validatePassword(values.password);
      if (!passwordResult.valid) {
        message.error(passwordResult.message);
        return;
      }

      setLoading(true);
      const response = await authAPI.registerWithWechat({
        openid: openid,
        username: values.username,
        email: values.email,
        password: values.password
      });

      if (response.data?.token) {
        message.success('注册成功');
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('current_user', JSON.stringify(response.data.user));
        setWechatModalVisible(false);
        setTimeout(() => {
          window.location.href = `/${response.data.user.username}/index/`;
        }, 100);
      }
    } catch (error) {
      message.error(error.response?.data?.message || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  // 关闭微信登录模态框
  const handleCloseWechatModal = () => {
    setWechatModalVisible(false);
    setWechatStatus('waiting');
    if (pollTimer) clearInterval(pollTimer);
  };

  // 测试接口：模拟扫码
  const handleTestScan = async () => {
    try {
      const testOpenid = 'test_openid_' + Date.now();
      console.log('模拟扫码，qrId:', qrId, 'openid:', testOpenid);

      const response = await authAPI.testScanWechat(qrId, { openid: testOpenid });
      console.log('测试扫码响应:', response);

      if (response.data?.status === 'scanned') {
        message.success('模拟扫码成功，请注册或绑定账号');
        setOpenid(response.data.openid);
        setWechatStatus('need_register'); // 直接显示注册表单
        if (pollTimer) clearInterval(pollTimer); // 停止轮询
      } else if (response.data?.status === 'confirmed') {
        message.success('登录成功');
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('current_user', JSON.stringify(response.data.user));
        setWechatModalVisible(false);
        if (pollTimer) clearInterval(pollTimer);
        setTimeout(() => {
          window.location.href = `/${response.data.user.username}/index/`;
        }, 100);
      }
    } catch (error) {
      console.error('模拟扫码失败:', error);
      message.error('模拟扫码失败: ' + (error.message || '未知错误'));
    }
  };

  const tabItems = [
    {
      key: '1',
      label: '登录',
      children: (
        <Form form={form} onFinish={onFinish}>
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' },
              { max: 20, message: '用户名最多20个字符' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input placeholder="用户名" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 8, message: '密码至少8位' },
              { pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/, message: '密码必须包含字母和数字' }
            ]}
          >
            <Input.Password placeholder="密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              登录
            </Button>
          </Form.Item>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="link" onClick={handleWechatLogin}>
              微信扫码登录
            </Button>
          </div>
        </Form>
      ),
    },
    {
      key: '2',
      label: '注册',
      children: (
        <Form onFinish={onRegister}>
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' },
              { max: 20, message: '用户名最多20个字符' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input placeholder="用户名" />
          </Form.Item>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="邮箱" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 8, message: '密码至少8位' },
              { pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/, message: '密码必须包含字母和数字' }
            ]}
          >
            <Input.Password placeholder="密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                }
              })
            ]}
          >
            <Input.Password placeholder="确认密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              注册
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: '3',
      label: '重置密码',
      children: (
        <Form onFinish={onResetPassword}>
          <Form.Item name="email" rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '请输入有效的邮箱地址' }]}>
            <Input placeholder="注册时使用的邮箱" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              发送重置链接
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    ...(resetToken ? [{
      key: '4',
      label: '设置新密码',
      children: (
        <Form onFinish={onConfirmResetPassword}>
          <Form.Item
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少8位' },
              { pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/, message: '密码必须包含字母和数字' }
            ]}
          >
            <Input.Password placeholder="新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                }
              })
            ]}
          >
            <Input.Password placeholder="确认新密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              重置密码
            </Button>
          </Form.Item>
        </Form>
      ),
    }] : []),
  ];

  return (
    <div style={{ width: '100%', maxWidth: 400, margin: '0 auto', padding: 24 }}>
      <Tabs activeKey={resetToken ? '4' : undefined} defaultActiveKey="1" centered items={tabItems} />

      {/* 微信扫码登录Modal */}
      <Modal
        title="微信扫码登录"
        open={wechatModalVisible}
        onCancel={handleCloseWechatModal}
        footer={null}
        width={500}
      >
        {wechatStatus === 'waiting' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {useWechat ? (
              // 使用真实微信登录 - 显示微信登录二维码图片
              <div>
                <div style={{ marginBottom: 16 }}>
                  <QRCode value={qrCode} size={200} />
                </div>
                <p style={{ color: '#666', marginBottom: 8 }}>请使用微信扫描二维码登录</p>
                <p style={{ color: '#999', fontSize: 12 }}>
                  扫码后微信会打开登录页面，请确认登录
                </p>
              </div>
            ) : (
              // 测试模式：显示qrId二维码
              <div>
                <div style={{ marginBottom: 16 }}>
                  <QRCode value={qrCode} size={200} />
                </div>
                <p style={{ color: '#666' }}>测试模式：请使用测试按钮模拟扫码</p>
                <p style={{ color: '#999', fontSize: 12 }}>二维码ID: {qrId}</p>
                <div style={{ marginTop: 20 }}>
                  <Button type="link" size="small" onClick={handleTestScan}>
                    测试：模拟扫码
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {wechatStatus === 'scanned' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <QRCode value={qrCode} size={200} />
            <p style={{ color: '#52c41a', marginTop: 16 }}>已扫码，请在手机上确认登录</p>
          </div>
        )}

        {wechatStatus === 'need_bind' && (
          <div style={{ padding: '20px' }}>
            <p style={{ textAlign: 'center', marginBottom: 20 }}>
              该微信账号尚未绑定，请输入已有账号信息进行绑定
            </p>
            <Form form={wechatForm} onFinish={handleBindAccount}>
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input placeholder="用户名" />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password placeholder="密码" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  绑定账号
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}

        {wechatStatus === 'need_register' && (
          <div style={{ padding: '20px' }}>
            <p style={{ textAlign: 'center', marginBottom: 20 }}>
              该微信账号尚未注册，请完善信息进行注册
            </p>
            <p style={{ textAlign: 'center', color: '#999', fontSize: 12, marginBottom: 20 }}>
              OpenID: {openid?.substring(0, 20)}...
            </p>
            <Form form={wechatForm} onFinish={handleWechatRegister}>
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { max: 20, message: '用户名最多20个字符' }
                ]}
              >
                <Input placeholder="用户名" />
              </Form.Item>
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input placeholder="邮箱" />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 8, message: '密码至少8位' }
                ]}
              >
                <Input.Password placeholder="密码" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  注册并绑定
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Iden;